// tarot-carousel.js
import EventEmitter from './core/lib/event-emitter.js';

// core managers
import DragHandler from './core/drag-handler.js';
import EffectManager from './core/effect-manager.js';
import OptionsManager from './core/options-manager.js';
import SlideManager from './core/slide-manager.js';
import TrackAnimator from './core/track-animator.js';
import TransitionManager from './core/transition-manager.js';
import WindowEvents from './core/window-events.js';
import FrameEngine from './core/frame-engine.js';

// data + events
import DataStore from './core/data-store.js';
import { EVENTS } from './core/events.js';

// utilities
import {
	calculateWidths,
	calculatePageCount,
	calculateTransformPoints,
	calculateSnapPoints,
} from './core/metrics.js';
import { getTrackPosForIndex } from './core/utils/metrics.js';
import { utils, resolveVisibilityBoundsElement } from './core/utils/utils.js';

// core effects (always included)
import CarouselEffect from './effects/carousel.js';
import FadeEffect from './effects/fade.js';

// core plugins (always included)
import AsNavFor from './plugins/as-nav-for.js';
import SyncWith from './plugins/sync-with.js';
import Autoplay from './plugins/autoplay.js';
import Buttons from './plugins/buttons.js';
import Pagination from './plugins/pagination.js';

/** 🔮 ✨ 🕯️ 🍄 🌙 ⭐ TAROT ⭐ 🌙 🍄 🕯️ ✨ 🔮 */
export default class Tarot extends HTMLElement {
	// ---------------------------------------------------------------------
	// static registries & helpers
	// ---------------------------------------------------------------------

	/** core effects (always included) */
	static effects = {
		carousel: CarouselEffect,
		fade: FadeEffect,
	};

	/** @type {Array<Function>} core plugins (always included) */
	static plugins = [AsNavFor, SyncWith, Autoplay, Buttons, Pagination];

	/** @type {Map<string, Function>} optional plugins registered via registerPlugin() */
	static optionalPlugins = new Map();

	/** @type {number} count of carousel instances created */
	static instanceCount = 0;

	/** @type {object} accumulated default options from registered plugins */
	static pluginDefaultOptions = {};

	/**
	 * register an effect class using a canonical key
	 * prefers a static identifier (effectName/slug/key), falls back to class name
	 * @param {Function} effectClass
	 */
	static registerEffect(effectClass) {
		const _ = this;

		// basic guard
		if (!effectClass) {
			console.warn('tarot-carousel.registerEffect requires an effect class');
			return;
		}

		// prefer explicit static id
		let effectName = effectClass.effectName;

		// final sanity check
		if (!effectName) {
			console.warn(
				'tarot-carousel.registerEffect: effect must have a static string id (e.g., effectName)'
			);
			return;
		}

		// normalize to canonical key
		const key = String(effectName)
			.trim()
			.toLowerCase()
			.replace(/effect$/, '');

		// warn on overwrite
		if (_.effects[key]) {
			console.warn(`tarot-carousel: overwriting existing effect '${key}'`);
		}

		// add effect class to constructor effects object
		_.effects[key] = effectClass;

		// accumulate effect default options into the same merge pipeline as plugins
		if (effectClass.defaultOptions && typeof effectClass.defaultOptions === 'object') {
			_.pluginDefaultOptions = utils.deepMerge(_.pluginDefaultOptions, effectClass.defaultOptions);
		}

		// notify listeners (e.g., EffectManager instances) that a new effect is available
		try {
			if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
				window.dispatchEvent(
					new CustomEvent('tarot:effect-registered', { detail: { effectName: key } })
				);
			}
		} catch (e) {
			// no-op if CustomEvent/window is unavailable
		}
	}

	/**
	 * derive a canonical key from a plugin class
	 * prefers pluginName, falls back to class name
	 * @param {Function} pluginClass
	 * @returns {string|null}
	 */
	static #getPluginKey(pluginClass) {
		let name = pluginClass.pluginName || pluginClass.name;
		if (!name) return null;
		return String(name)
			.trim()
			.toLowerCase()
			.replace(/plugin$/, '');
	}

	/**
	 * register an optional plugin class
	 * adds it to the static plugins array for future instances and
	 * dispatches an event so already-connected instances can pick it up
	 * @param {Function} pluginClass
	 */
	static registerPlugin(pluginClass) {
		const _ = this;

		if (!pluginClass) {
			console.warn('tarot-carousel.registerPlugin requires a plugin class');
			return;
		}

		const key = _.#getPluginKey(pluginClass);
		if (!key) {
			console.warn(
				'tarot-carousel.registerPlugin: plugin must have a static pluginName or class name'
			);
			return;
		}

		// warn on overwrite, remove old class from plugins array
		if (_.optionalPlugins.has(key)) {
			console.warn(`tarot-carousel: overwriting existing optional plugin '${key}'`);
			const oldPlugin = _.optionalPlugins.get(key);
			const idx = _.plugins.indexOf(oldPlugin);
			if (idx !== -1) _.plugins.splice(idx, 1);
		}

		_.optionalPlugins.set(key, pluginClass);

		// collect plugin default options
		if (pluginClass.defaultOptions && typeof pluginClass.defaultOptions === 'object') {
			_.pluginDefaultOptions = utils.deepMerge(_.pluginDefaultOptions, pluginClass.defaultOptions);
		}

		// add to static plugins array (guarded against duplicates)
		if (!_.plugins.includes(pluginClass)) {
			_.plugins.push(pluginClass);
		}

		// notify already-connected instances
		try {
			if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
				window.dispatchEvent(
					new CustomEvent('tarot:plugin-registered', { detail: { pluginName: key, pluginClass } })
				);
			}
		} catch (e) {
			// no-op if CustomEvent/window is unavailable
		}
	}

	/**
	 * register a plugin class to be initialized on new instances
	 * delegates to registerPlugin so late-loaded plugins also reach connected instances
	 * @param {Function} plugin
	 * @returns {typeof Tarot}
	 */
	static use(plugin) {
		this.registerPlugin(plugin);
		return this;
	}

	// ---------------------------------------------------------------------
	// private fields
	// ---------------------------------------------------------------------

	#eventEmitter; // shared emitter for this instance
	#store; // datastore for options/state/widths/slides
	#ctx; // frozen module context passed to managers

	#transitionManager;
	#trackAnimator;
	#slideManager;
	#optionsManager;
	#effectManager;
	#dragHandler;
	#windowEvents;
	#frameEngine;

	#pluginInstances = [];
	#initializedPluginKeys = new Set();
	#pluginRegisteredHandler = null;
	#coreHandlers = null;
	#isRefreshing = false;
	#viewport;
	#track;
	#announcements;

	// ---------------------------------------------------------------------
	// lifecycle
	// ---------------------------------------------------------------------

	/** creates a new tarotcarousel instance */
	constructor() {
		super();
		const _ = this;

		// set a unique id for this carousel instance
		if (!_.id) {
			_.id = `tarot-carousel-${Tarot.instanceCount}`;
		}
		Tarot.instanceCount++;

		// initialize private emitter
		_.#eventEmitter = new EventEmitter();

		// initialize store with default shapes
		_.#store = new DataStore(_.#eventEmitter);
	}

	/** custom element connected lifecycle hook */
	async connectedCallback() {
		const _ = this;

		// make sure tarot-slide component is registered
		if (!customElements.get('tarot-slide')) {
			await customElements.whenDefined('tarot-slide');

			// wait one frame to ensure children are connected
			await new Promise(requestAnimationFrame);
		}

		// re-create if this is a reconnection after disconnectedCallback
		if (!_.#eventEmitter) _.#eventEmitter = new EventEmitter();
		if (!_.#store) _.#store = new DataStore(_.#eventEmitter);

		// query and normalize required child elements
		_.#queryDOMElements();

		// make carousel focusable for keyboard navigation
		_.setAttribute('tabindex', '0');
		_.setAttribute('role', 'group');
		_.setAttribute('aria-roledescription', 'carousel');

		// now that dom refs exist, build the shared context
		_.#ctx = _.#createModuleContext();
		const ctx = _.#ctx; // local ref for faster property access

		// create managers (all receive ctx, not the whole carousel)
		_.#optionsManager = new OptionsManager(ctx);
		_.#windowEvents = new WindowEvents(ctx);
		_.#slideManager = new SlideManager(ctx);
		_.#effectManager = new EffectManager(ctx, _.constructor.effects);
		_.#dragHandler = new DragHandler(ctx);
		_.#trackAnimator = new TrackAnimator(ctx);
		_.#transitionManager = new TransitionManager(ctx, _.#trackAnimator);
		_.#frameEngine = new FrameEngine(ctx);

		// cross-module wiring
		_.#bindCoreEvents();

		// load effect and collect slides
		_.#effectManager.loadCurrentEffect();

		// compute initial widths + page count
		_.#recomputeLayout();

		// re-initialize effect with correct widths (effects load before widths are calculated)
		_.#effectManager.reInit();

		// initialize plugins (errors should not break the instance)
		const plugins = _.constructor.plugins;
		plugins.forEach((PluginClass) => {
			try {
				// derive key and skip if already initialized (prevents duplicate-key plugins)
				const key = _.constructor.#getPluginKey(PluginClass);
				if (key && _.#initializedPluginKeys.has(key)) return;

				const pluginInstance = new PluginClass(ctx);
				_.#pluginInstances.push(pluginInstance);
				if (key) _.#initializedPluginKeys.add(key);
			} catch (error) {
				console.error(`plugin ${PluginClass?.name || '(anonymous)'} failed to initialize:`, error);
			}
		});

		// listen for plugins registered after this instance connected
		_.#pluginRegisteredHandler = (e) => {
			const { pluginName, pluginClass } = e.detail;
			if (_.#initializedPluginKeys.has(pluginName)) return; // already initialized
			try {
				const pluginInstance = new pluginClass(ctx);
				_.#pluginInstances.push(pluginInstance);
				_.#initializedPluginKeys.add(pluginName);

				// re-apply options so plugin defaults are merged into the chain
				if (pluginClass.defaultOptions) {
					_.#optionsManager.setUserOptions({});
				}
			} catch (error) {
				console.error(`plugin ${pluginName} failed to late-initialize:`, error);
			}
		};
		window.addEventListener('tarot:plugin-registered', _.#pluginRegisteredHandler);

		// perform first paint by jumping to initial slide without animation
		const initial = _.#store.getOptions().initialIndex ?? 0;
		_.jumpToSlide(initial);

		_.#frameEngine.requestFrame();

		// signal that the carousel is fully initialized and ready
		_.#eventEmitter.emit(EVENTS.carousel.ready, {});
	}

	// ---------------------------------------------------------------------
	// context / bus
	// ---------------------------------------------------------------------

	/**
	 * build and freeze a shared module context
	 * @returns {object} ctx
	 */
	#createModuleContext() {
		const _ = this;

		// keep this surface area tiny and explicit
		return Object.freeze({
			// pub/sub
			emitter: _.#eventEmitter,
			events: EVENTS,

			// data store
			store: _.#store,

			// plugin defaults (getter so late-registered plugins are always picked up)
			getPluginDefaults: () => Tarot.pluginDefaultOptions,

			// effect class lookup (for options-manager to read static rules)
			getEffectClass: (name) => _.constructor.effects[name] || null,

			// utilities
			utils,

			// dom refs used during rendering
			carousel: _, // host element (custom element instance)
			viewport: _.#viewport,
			track: _.#track,
			announcements: _.#announcements,

			// minimal imperative api
			commands: {
				goToSlide: (index, velocity = 0, movementType = 'animate') =>
					_.goToSlide(index, velocity, movementType),
				jumpToSlide: (index) => _.jumpToSlide(index),
				next: (velocity = 0) => _.next(velocity),
				previous: (velocity = 0) => _.previous(velocity),
				goToPage: (page, velocity = 0, movementType = 'animate') =>
					_.goToPage(page, velocity, movementType),
				jumpToPage: (page) => _.jumpToPage(page),
				settleTrack: () => _.#settleTrack(),
				stopAnimations: () => _.#trackAnimator.stop(),
				requestTrackPosition: (position) => _.requestTrackPosition(position),
				getEffect: () => _.#effectManager.getEffect(),
				getSlideManager: () => _.#slideManager,
				requestFrame: () => _.#frameEngine.requestFrame(0),
				driveTrackPosition: ({
					deltaPx,
					positionPercent,
					velocity = 0,
					movementType = 'scroll',
				} = {}) => {
					_.#trackAnimator.drivePosition({ deltaPx, positionPercent, velocity, movementType });
				},
			},
		});
	}

	// ---------------------------------------------------------------------
	// dom setup
	// ---------------------------------------------------------------------

	/**
	 * find or create required child elements
	 * ensures there is a <tarot-viewport> wrapping a <tarot-track>
	 * creates announcement element for screen reader navigation feedback
	 */
	#queryDOMElements() {
		const _ = this;

		// query viewport and track
		let viewport = _.querySelector(':scope tarot-viewport');
		let track = _.querySelector(':scope tarot-track');

		// throw if both are missing
		if (!viewport && !track) {
			throw new Error('tarot-carousel: missing both <tarot-viewport> and <tarot-track> elements');
		}

		// ensure viewport wraps track
		if (track && !viewport) {
			viewport = document.createElement('tarot-viewport');
			track.parentNode.insertBefore(viewport, track);
			viewport.appendChild(track);
		}
		if (viewport && !track) {
			track = document.createElement('tarot-track');
			while (viewport.firstChild) track.appendChild(viewport.firstChild);
			viewport.appendChild(track);
		}

		// create announcement element for screen reader navigation feedback
		const announcements = document.createElement('div');
		announcements.className = 'tarot-visually-hidden tarot-announcements';
		announcements.id = `tarot-announcements-${_.id}`;
		announcements.setAttribute('aria-live', 'polite');
		announcements.setAttribute('aria-atomic', 'true');

		// insert as first child for screen reader discovery
		_.insertBefore(announcements, _.firstChild);

		_.#viewport = viewport;
		_.#track = track;
		_.#announcements = announcements;
	}

	// ---------------------------------------------------------------------
	// cross-module wiring (orchestration)
	// ---------------------------------------------------------------------

	/** subscribe to core events and coordinate managers */
	#bindCoreEvents() {
		const _ = this;
		const { emitter, events } = _.#ctx;

		const debouncedRefreshLayout = _.#ctx.utils.debounce(() => _.#refreshLayout(), 4);

		_.#coreHandlers = {
			debouncedRefreshLayout,
			effectLoaded: ({ effectName }) => {
				// Skip during #refreshLayout() — that path already handles layout + effect reInit
				if (_.#isRefreshing) return;

				// Only merge options when the loaded effect matches the user's desired effect.
				// During fallback scenarios (e.g., user wants 'spotlight' but it isn't registered
				// yet, so carousel loads instead), skip — otherwise applyMergedOptions() writes
				// effect:'spotlight' to the store, EffectManager sees the name mismatch, re-loads
				// spotlight → falls back to carousel → effect:loaded → infinite loop.
				const desiredEffect = _.#optionsManager.userOptions?.effect;
				if (effectName !== desiredEffect) return;

				// Force full options merge — picks up effect rules AND defaultOptions.
				// Same path used during normal init (options-manager.js applyMergedOptions).
				_.#optionsManager.applyMergedOptions();

				// Cancel the debounced refresh that applyMergedOptions just triggered
				// via options:changed. We do the layout work ourselves below.
				debouncedRefreshLayout.cancel();

				// Reinit the effect instance so it re-reads options from the store
				_.#effectManager.getEffect()?.reInit?.();

				_.#recomputeLayout();
				_.jumpToSlide(_.#store.getState().renderIndex);
			},
			slidesClick: ({ index }) => {
				_.#store.setState({ selectedIndex: index });
				if (_.#store.getOptions().goToSelectedSlide) _.goToSlide(index);
			},
			keyboardArrow: ({ direction }) => {
				direction === -1 ? _.previous() : _.next();
			},
		};

		emitter.on(events.store.optionsChanged, _.#coreHandlers.debouncedRefreshLayout);
		emitter.on(events.window.resize, _.#coreHandlers.debouncedRefreshLayout);
		emitter.on(events.store.slidesChanged, _.#coreHandlers.debouncedRefreshLayout);
		emitter.on(events.effect.loaded, _.#coreHandlers.effectLoaded);
		emitter.on(events.slides.click, _.#coreHandlers.slidesClick);
		emitter.on(events.keyboard.arrow, _.#coreHandlers.keyboardArrow);
	}

	/** unbind all core event handlers registered in #bindCoreEvents */
	#unbindCoreEvents() {
		const _ = this;
		if (!_.#coreHandlers || !_.#ctx) return;

		const { emitter, events } = _.#ctx;

		emitter.off(events.store.optionsChanged, _.#coreHandlers.debouncedRefreshLayout);
		emitter.off(events.window.resize, _.#coreHandlers.debouncedRefreshLayout);
		emitter.off(events.store.slidesChanged, _.#coreHandlers.debouncedRefreshLayout);
		emitter.off(events.effect.loaded, _.#coreHandlers.effectLoaded);
		emitter.off(events.slides.click, _.#coreHandlers.slidesClick);
		emitter.off(events.keyboard.arrow, _.#coreHandlers.keyboardArrow);

		// cancel any pending debounced call
		_.#coreHandlers.debouncedRefreshLayout?.cancel?.();

		_.#coreHandlers = null;
	}

	/**
	 * recompute layout widths and page count using current slides/options
	 * @param {object} [optOverride] - optional options to use for this pass
	 */
	#recomputeLayout(optOverride) {
		const _ = this;
		const options = optOverride || _.#store.getOptions();
		const slides = _.#store.getSlides();

		// resolve the visibility bounds element (used only by getSlidesInViewport)
		const boundsEl = resolveVisibilityBoundsElement(
			options.visibilityBoundsElement,
			_,
			_.#viewport
		);

		// recalc width metrics
		const widths = calculateWidths({
			viewportEl: _.#viewport,
			boundsEl,
			options,
			slideCount: slides.length,
		});
		_.#store.setWidths(widths);

		// recalc transform points (after widths are updated)
		const transformPoints = calculateTransformPoints(_.#ctx);
		_.#store.setTransformPoints(transformPoints);

		// recalc page count
		const pageCount = calculatePageCount({
			loop: options.loop,
			slidesPerMove: options.slidesPerMove,
			slidesPerView: options.slidesPerView,
			slideCount: slides.length,
			centerSelectedSlide: options.centerSelectedSlide,
		});

		// recalc canLoop (accounts for effect buffer requirements)
		const loopBuffer = options.rules.loopBuffer;
		const canLoop = _.#ctx.utils.canLoop(slides.length, options, loopBuffer);

		_.#store.setState({ pageCount, canLoop });

		// recalc snap points (after pageCount and canLoop are set)
		const state = _.#store.getState();
		const { snapPoints, firstSnapPoint, lastSnapPoint } = calculateSnapPoints(
			widths,
			options,
			state
		);
		_.#store.setState({ snapPoints, firstSnapPoint, lastSnapPoint });
	}

	/**
	 * Refreshes layout metrics, reinits effect, and maintains slide position
	 * Used by options/resize/slides change handlers
	 */
	#refreshLayout() {
		const _ = this;
		_.#isRefreshing = true;
		_.#recomputeLayout();
		_.#effectManager.reInit();
		_.#isRefreshing = false;
		_.jumpToSlide(_.#store.getState().renderIndex);
	}

	// ---------------------------------------------------------------------
	// public api (pure model updates; no direct dom writes)
	// ---------------------------------------------------------------------

	/**
	 * advance to the next page
	 * @param {number} [velocity=0]
	 */
	next(velocity = 0) {
		this.goToPage(this.state.pageIndex + 1, velocity);
	}

	/**
	 * go back to the previous page
	 * @param {number} [velocity=0]
	 */
	previous(velocity = 0) {
		this.goToPage(this.state.pageIndex - 1, velocity);
	}

	/**
	 * settle the track back to current position
	 * used after drag below threshold
	 */
	#settleTrack(velocity = 0) {
		const state = this.#store.getState();

		this.#eventEmitter.emit(EVENTS.movement.requested, {
			index: state.renderIndex,
			pageIndex: state.pageIndex,
			velocity: velocity,
			movementType: 'settle',
		});
	}

	/**
	 * animate to a specific slide index (logical)
	 * @param {number} index
	 * @param {number} [velocity=0]
	 */
	goToSlide(index, velocity = 0, movementType = 'animate') {
		// exit if no index passed in
		if (index === undefined) return;

		const _ = this;

		const state = _.#store.getState();
		const slides = _.#store.getSlides();
		const slideCount = state.slideCount ?? slides.length;
		if (slideCount === 0) return;

		// bounds + looping (use state.canLoop which accounts for effect buffer requirements)
		if (index < 0) {
			index = state.canLoop ? ((index % slideCount) + slideCount) % slideCount : 0;
			if (!state.canLoop && !velocity) velocity = 10;
		} else if (index >= slideCount) {
			index = state.canLoop ? index % slideCount : slideCount - 1;
			if (!state.canLoop && !velocity) velocity = -10;
		}

		// emit movement request event - TransitionManager handles state + animation
		_.#eventEmitter.emit(EVENTS.movement.requested, {
			index,
			pageIndex: _.#getPageIndexForSlide(index),
			velocity,
			movementType,
		});
	}

	/**
	 * jump (no animation) to a specific slide
	 * @param {number} index
	 */
	jumpToSlide(index) {
		this.goToSlide(index, 0, 'jump');
	}

	/**
	 * go to a page (converts page -> slide) with animation
	 * @param {number} newPage
	 * @param {number} [velocity=0]
	 * @param {string} [movementType='animate']
	 */
	goToPage(newPage, velocity = 0, movementType = 'animate') {
		if (newPage === undefined) return;

		const _ = this;
		const state = _.#store.getState();
		const options = _.#store.getOptions();
		if (state.pageCount === 0) return;

		// clamp or wrap (use state.canLoop which accounts for effect buffer requirements)
		if (newPage < 0) {
			newPage = state.canLoop
				? ((newPage % state.pageCount) + state.pageCount) % state.pageCount
				: 0;
			if (!state.canLoop && !velocity) velocity = 10;
		} else if (newPage >= state.pageCount) {
			newPage = state.canLoop ? 0 : state.pageCount - 1;
			if (!state.canLoop && !velocity) velocity = -10;
		}

		// every goToPage is actually converted to a goToSlide
		const newIndex = newPage * (options.slidesPerMove ?? 1);
		_.goToSlide(newIndex, velocity, movementType);
	}

	/**
	 * jump directly to a page (no animation)
	 * @param {number} newPage
	 */
	jumpToPage(newPage) {
		this.goToPage(newPage, 0, 'jump');
	}

	/**
	 * request a specific track position (continuous positioning system)
	 * @param {number|string} position - position to move to:
	 *   - number: treated as percentage (0-100)
	 *   - string ending in '%': percentage (e.g., '50%')
	 *   - string ending in 'px': pixel position (e.g., '200px')
	 *   - other string: treated as percentage number
	 */
	requestTrackPosition(position) {
		const _ = this;

		// exit if no position passed in
		if (position === undefined || position === null) return;

		let trackPosition = 0;

		// parse the position input
		if (typeof position === 'string') {
			if (position.endsWith('px')) {
				// pixel position
				trackPosition = parseFloat(position.replace('px', ''));
			} else if (position.endsWith('%')) {
				// percentage position
				const percent = parseFloat(position.replace('%', ''));
				trackPosition = _.#convertPercentToTrackPos(percent);
			} else {
				// assume it's a number string (percentage)
				const percent = parseFloat(position);
				trackPosition = _.#convertPercentToTrackPos(percent);
			}
		} else if (typeof position === 'number') {
			// assume number is percentage
			trackPosition = _.#convertPercentToTrackPos(position);
		} else {
			console.warn('requestTrackPosition: invalid position type', position);
			return;
		}

		// emit movement request event to follow the same pattern as a jump
		_.#eventEmitter.emit(EVENTS.movement.requested, {
			trackPosition, // direct track position instead of index
			velocity: 0,
			movementType: 'jump',
		});
	}

	/**
	 * convert percentage (0-100) to track position based on last page position
	 * @param {number} percent - percentage from 0 to 100
	 * @returns {number} track position in pixels
	 * @private
	 */
	#convertPercentToTrackPos(percent) {
		const _ = this;
		const state = _.#store.getState();
		const options = _.#store.getOptions();

		// ensure percent is within bounds
		percent = Math.max(0, Math.min(100, percent));

		// if no pages or only one page, return 0
		if (state.pageCount <= 1) return 0;

		// calculate the slide index of the last page
		const lastPageIndex = state.pageCount - 1;
		const lastPageSlideIndex = lastPageIndex * (options.slidesPerMove ?? 1);

		// get the track position for the last page
		const widths = _.#store.getWidths();
		const lastPageTrackPos = getTrackPosForIndex(lastPageSlideIndex, widths, options, state);

		// convert percentage to track position
		// 0% = position 0, 100% = lastPageTrackPos
		return (percent / 100) * lastPageTrackPos;
	}

	// ---------------------------------------------------------------------
	// internal helpers (state mutation + derived values)
	// ---------------------------------------------------------------------

	/** compute page index for a given slide index */
	#getPageIndexForSlide(slideIndex) {
		const options = this.#store.getOptions();
		const perMove = options.slidesPerMove ?? 1;
		return Math.floor(slideIndex / perMove);
	}

	// ---------------------------------------------------------------------
	// getters (public readonly snapshots and convenience)
	// ---------------------------------------------------------------------

	/** immutable runtime state snapshot */
	get state() {
		return this.#store.getState();
	}

	/** immutable visibility snapshot */
	get visibility() {
		return this.#store.getVisibility();
	}

	/** readonly slides (internal descriptors) */
	get slides() {
		return this.#store.getSlides();
	}

	/** immutable options snapshot */
	get options() {
		return this.#store.getOptions();
	}

	get viewport() {
		return this.#viewport;
	}

	get track() {
		return this.#track;
	}

	/** registered effect keys */
	get effects() {
		return Object.keys(this.constructor.effects);
	}

	/** user-provided options (before defaults applied) */
	get userOptions() {
		return this.#optionsManager.userOptions;
	}

	/** update options (merges with existing) */
	updateOptions(newOptions) {
		this.#optionsManager.setUserOptions?.(newOptions);
	}

	/** current slide index */
	get index() {
		return this.state.renderIndex;
	}

	/** current page index */
	get page() {
		return this.state.pageIndex;
	}

	/** selected slide index */
	get selectedIndex() {
		return this.state.selectedIndex;
	}
	setSelectedIndex(index) {
		const _ = this;
		const slides = _.#store.getSlides();
		const slideCount = slides.length;

		// bounds check
		if (index < 0 || index >= slideCount) {
			console.warn(`setSelectedIndex: index ${index} out of bounds (0-${slideCount - 1})`);
			return;
		}

		// update selected index in store (triggers selection events)
		_.#store.setState({ selectedIndex: index });

		// should we go to slide on select?
		if (_.#store.getOptions().goToSelectedSlide) {
			_.goToSlide(index);
		}
	}
	/** currently selected slide element */
	get selectedSlide() {
		return this.#store.getSlides()[this.state.selectedIndex];
	}
	getSlideAtIndex(index) {
		return this.#store.getSlides()[index];
	}

	/**
	 * Add a slide to the carousel
	 * @param {string|HTMLElement} element - HTML string or DOM element to add
	 * @param {number} [index] - Optional index to insert at (appends to end if omitted)
	 */
	addSlide(element, index) {
		this.#slideManager.addSlide(element, index);
	}

	/**
	 * Remove a slide from the carousel
	 * @param {number} index - Index of slide to remove
	 */
	removeSlide(index) {
		this.#slideManager.removeSlide(index);
	}

	/**
	 * Set slide state for visibility/transitions
	 * @param {string|number} indexOrState - State to apply to all slides, or slide index
	 * @param {string} [state] - State when first param is index ('active', 'hidden', 'disabled')
	 */
	setSlideState(indexOrState, state) {
		const slides = this.#store.getSlides();

		if (typeof indexOrState === 'string') {
			// Apply to all non-disabled slides
			slides.forEach((slide) => {
				if (slide.getAttribute('state') !== 'disabled') {
					slide.setAttribute('state', indexOrState);
				}
			});
		} else {
			// Apply to specific slide by index
			const slide = slides[indexOrState];
			if (slide) slide.setAttribute('state', state);
		}
	}

	// ---------------------------------------------------------------------
	// event subscription api (public)
	// ---------------------------------------------------------------------

	/**
	 * subscribe to an event
	 * External listeners are deferred to next tick to prevent blocking the carousel
	 * @param {string} event
	 * @param {Function} listener
	 */
	on(event, listener) {
		this.#eventEmitter.on(event, listener, { defer: true });
	}

	/**
	 * unsubscribe from an event
	 * @param {string} event
	 * @param {Function} listener
	 */
	off(event, listener) {
		this.#eventEmitter.off(event, listener);
	}

	// ---------------------------------------------------------------------
	// teardown
	// ---------------------------------------------------------------------

	/** custom element disconnected lifecycle hook — full teardown */
	disconnectedCallback() {
		const _ = this;

		// remove late-registration listener
		if (_.#pluginRegisteredHandler) {
			window.removeEventListener('tarot:plugin-registered', _.#pluginRegisteredHandler);
			_.#pluginRegisteredHandler = null;
		}
		_.#initializedPluginKeys.clear();

		// destroy plugins
		_.#pluginInstances.forEach((plugin) => plugin?.destroy?.());
		_.#pluginInstances.length = 0;

		// unbind core events
		_.#unbindCoreEvents();

		// destroy all managers (reverse creation order)
		_.#frameEngine?.destroy?.();
		_.#transitionManager?.destroy?.();
		_.#trackAnimator?.destroy?.();
		_.#dragHandler?.destroy?.();
		_.#effectManager?.destroy?.();
		_.#slideManager?.destroy?.();
		_.#windowEvents?.destroy?.();
		_.#optionsManager?.destroy?.();

		// null manager refs
		_.#frameEngine = null;
		_.#transitionManager = null;
		_.#trackAnimator = null;
		_.#dragHandler = null;
		_.#effectManager = null;
		_.#slideManager = null;
		_.#windowEvents = null;
		_.#optionsManager = null;

		// clean up ARIA attributes
		_.removeAttribute('role');
		_.removeAttribute('aria-roledescription');

		// clean up announcements element
		if (_.#announcements?.parentNode) {
			_.#announcements.parentNode.removeChild(_.#announcements);
		}
		_.#announcements = null;

		// destroy store (releases slide DOM refs)
		_.#store?.destroy?.();
		_.#store = null;

		// destroy emitter (clears all remaining listeners)
		_.#eventEmitter?.destroy?.();
		_.#eventEmitter = null;

		// clear context
		_.#ctx = null;
	}
}

// Accumulate defaults from core effects (these bypass registerEffect)
for (const effectClass of Object.values(Tarot.effects)) {
	if (effectClass.defaultOptions && typeof effectClass.defaultOptions === 'object') {
		Tarot.pluginDefaultOptions = utils.deepMerge(
			Tarot.pluginDefaultOptions,
			effectClass.defaultOptions
		);
	}
}
